import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Input } from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import {
  checkEmail,
  checkPhone,
  getProfile,
  registerEmail,
  registerPhone,
  verificationRequestEmail,
  verificationRequestPhone,
} from "../../api";
import { Link, useHistory } from "react-router-dom";
import EnterOtp from "../../components/common/EnterOtp";
import { commonUtil } from "../../utils";
import {
  CHECK_IDENTIFIER,
  PATH_POLICY,
  PATH_TERM_AND_CONDITION,
  VERIFICATION,
} from "../../constants";
import RegisterFacebook from "./RegisterFacebook";
import RegisterGoogle from "./RegisterGoogle";
import RegisterApple from "./RegisterApple";
import { useAuth } from "../../contexts/authContext";
import PhoneNotVerifyPopup from "../../components/common/PhoneNotVerifyPopup";
import { ValidationForm } from "../../common/interfaces";
import styles from "./register.module.css";
import { convertPhone, isEmailValid, isPhoneValid } from "../../utils/common";

interface Props {}

const Register: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(["common", "notification"]);
  const auth = useAuth();
  const history = useHistory();
  const [validateCaptcha, setValidateCaptcha] = useState<ValidationForm | null>(
    null
  );
  const [popupToShow, setPopupToShow] = useState<
    "ENTER_OTP" | "NOTIFY_PHONE" | null
  >(null);
  const [otpType, setOtpType] = useState<"email" | "phone">("email");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [verificationId, setVerificationId] = useState<number>(0);
  const [phoneNotVerify, setPhoneNotVerify] = useState<{
    phone: string;
    emails: string[];
  }>();
  const recaptchaRef: any = React.createRef();

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const callbackUrl = query.get("callbackUrl");
    if (callbackUrl) {
      const callbackDomain = new URL(callbackUrl).hostname;
      if (
        !process.env.REACT_APP_CALLBACK_DOMAINS ||
        !process.env.REACT_APP_CALLBACK_DOMAINS.split(",").includes(
          callbackDomain
        )
      ) {
        commonUtil.showMessage("error", "Invalid Callback URL");
        history.push("/login");
        return;
      }
    }
    if (query.get("emailOrPhone") || "") {
      query.delete("emailOrPhone");
      window.history.replaceState(
        null,
        "",
        `${window.location.origin}${window.location.pathname}${
          query.toString() ? `?${query.toString()}` : ""
        }`
      );
    }
  }, []);

  const onClickLogin = () => {
    history.push(`/login${window.location.search}`);
  };

  const handleSubmit = async (values: any) => {
    // @ts-ignore
    const token = await recaptchaRef.current.getValue();
    console.log("token", token);
    // @ts-ignore
    recaptchaRef.current.reset();
    setValidateCaptcha(null);

    if (!token || token == "") {
      setValidateCaptcha({
        status: "error",
        text: t("notification:Vui lòng chọn Tôi không phải người máy"),
      });
      return false;
    }

    setIsLoading(true);
    const res = commonUtil.isEmailValid(form.getFieldValue("username"))
      ? await verificationRequestEmail({
          feature: VERIFICATION.FEATURE.REGISTER,
          email: values?.username,
          token: token,
        })
      : await verificationRequestPhone({
          feature: VERIFICATION.FEATURE.REGISTER,
          phone: commonUtil.convertPhone(values?.username),
          token: token,
        });
    setIsLoading(false);
    if (res.status === "error") {
      commonUtil.showMessage("error", res.message);
    } else {
      setVerificationId(res.data.verificationId);
      setOtpType(
        commonUtil.isEmailValid(form.getFieldValue("username"))
          ? "email"
          : "phone"
      );
      setPopupToShow("ENTER_OTP");
    }
  };

  const onConfirmOtpSuccess = async () => {
    if (commonUtil.isPhoneValid(form.getFieldValue("username"))) {
      const resCheckPhone = await checkPhone({
        phone: convertPhone(form.getFieldValue("username")),
      });
      if (resCheckPhone.status === "success") {
        if (resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.NOT_VERIFY) {
          if (resCheckPhone.data.emails.length) {
            setPhoneNotVerify({
              phone: form.getFieldValue("username"),
              emails: resCheckPhone.data.emails,
            });
            setPopupToShow("NOTIFY_PHONE");
            setIsLoading(false);
          }
        } else if (
          resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.VERIFIED
        ) {
          commonUtil.showMessage("error", "Số điện thoại đã tồn tại");
        } else if (
          resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.NOT_EXIST
        ) {
          await requestRegister();
        }
      }
    } else if (commonUtil.isEmailValid(form.getFieldValue("username"))) {
      const resCheckEmail = await checkEmail({
        email: form.getFieldValue("username"),
      });
      if (resCheckEmail.status === "success") {
        if (resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.NOT_VERIFY) {
          await requestRegister();
        } else if (
          resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.VERIFIED
        ) {
          commonUtil.showMessage("error", "Email đã tồn tại");
        } else if (
          resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.NOT_EXIST
        ) {
          await requestRegister();
        }
      }
    }
  };

  const requestRegister = async () => {
    const resRegister =
      otpType === "email"
        ? await registerEmail({
            verificationId,
            email: form.getFieldValue("username"),
            password: form.getFieldValue("password"),
          })
        : await registerPhone({
            verificationId,
            phone: commonUtil.convertPhone(form.getFieldValue("username")),
            password: form.getFieldValue("password"),
          });
    if (resRegister.status === "error") {
      commonUtil.showMessage("error", resRegister.message);
      return;
    }
    onRegisterSuccess(
      resRegister.data.access_token,
      resRegister.data.refresh_token,
      resRegister.data.expires_in
    );
  };

  const onRegisterSuccess = async (
    accessToken: string,
    refreshToken: string,
    expires: number
  ) => {
    commonUtil.saveSession(accessToken, refreshToken, expires);

    const query = new URLSearchParams(window.location.search);
    const callbackUrl = query.get("callbackUrl");
    const state = query.get("state");
    if (!callbackUrl) {
      const resProfile = await getProfile();
      if (resProfile.status === "success") {
        auth.login(resProfile.data);
        history.push("/profile");
      }
      return;
    }
    window.location.href = `${callbackUrl}${state ? `?state=${state}` : ""}`;
  };

  const onClickContinueRegister = async () => {
    const resRegister =
      otpType === "email"
        ? await registerEmail({
            verificationId,
            email: form.getFieldValue("username"),
            password: form.getFieldValue("password"),
          })
        : await registerPhone({
            verificationId,
            phone: commonUtil.convertPhone(form.getFieldValue("username")),
            password: form.getFieldValue("password"),
          });
    if (resRegister.status === "error") {
      commonUtil.showMessage("error", resRegister.message);
      return;
    }
    onRegisterSuccess(
      resRegister.data.access_token,
      resRegister.data.refresh_token,
      resRegister.data.expires_in
    );
  };

  return (
    <>
      <div className="container mx-auto mt-16 md:mt-40 mb-16 md:mb-40 main">
        <div className="bg-white mx-auto p-16 max-w-[472px] rounded-sm overflow-hidden">
          <p className="text-2xl font-semibold">{t("common:Đăng ký")}</p>

          <div className="my-36">
            <Form
              form={form}
              size="large"
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                name="username"
                label={t("common:Email hoặc số điện thoại")}
                required
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error("Vui lòng nhập email hoặc số điện thoại")
                        );
                      } else if (!isEmailValid(value) && !isPhoneValid(value)) {
                        return Promise.reject(
                          new Error(
                            "Email hoặc số điện thoại không đúng định dạng"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  type="text"
                  placeholder={t("common:Email hoặc số điện thoại")}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label={t("common:Mật khẩu")}
                required
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error("Vui lòng nhập mật khẩu")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                  {
                    pattern:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message:
                      "Mật khẩu phải có ít nhất 8 ký tự, bao gồm chữ hoa, chữ thường, chữ số và ký tự đặc biệt.",
                  },
                ]}
              >
                <Input.Password
                  placeholder={t("common:Mật khẩu")}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item
                name="re-password"
                label={t("common:Nhập lại mật khẩu")}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value?.trim() !== form.getFieldValue("password")) {
                        return Promise.reject(
                          new Error(t("notification:Mật khẩu không khớp"))
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                required
              >
                <Input.Password
                  placeholder={t("common:Nhập lại mật khẩu")}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item
                validateStatus={validateCaptcha?.status}
                help={validateCaptcha?.text}
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Ld_n7EZAAAAAMif3OxoZ1kvUvyBN3uXTXMTz4w7"
                />
              </Form.Item>
              <p className="my-24">
                {t(
                  "common:Chúng tôi sẽ gửi mã xác thực cho bạn để đăng ký tài khoản"
                )}
              </p>
              <Button
                type="primary"
                block={true}
                htmlType="submit"
                loading={isLoading}
                className="!bg-red-primary"
              >
                {t("common:Tiếp tục")}
              </Button>
              <div className={styles.orWrapper}>
                <span className={styles.or}>{t("common:Hoặc")}</span>
              </div>
              <div style={{ marginBottom: "30px" }}>
                {/* <div style={{ marginBottom: "16px" }}>
                  <RegisterFacebook onRegisterSuccess={onRegisterSuccess} />
                </div> */}
                <div style={{ marginBottom: "16px" }}>
                  <RegisterGoogle onRegisterSuccess={onRegisterSuccess} />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <RegisterApple onRegisterSuccess={onRegisterSuccess} />
                </div>
              </div>
              <p>
                {t("common:Bạn đã có tài khoản?")}&nbsp;
                <a className={styles.loginLink} onClick={onClickLogin}>
                  {t("common:Đăng nhập")}
                </a>
              </p>
              <p>
                {t("common:Tiếp tục nghĩa là bạn đồng ý với các")}&nbsp;
                <a
                  className={styles.termAndPolicyLink}
                  target="_blank"
                  href={PATH_POLICY}
                >
                  {t("common:Điều khoản và Điều kiện")}
                </a>
                ,&nbsp;{t("common:cũng như các")}&nbsp;
                <a
                  className={styles.termAndPolicyLink}
                  target="_blank"
                  href={PATH_TERM_AND_CONDITION}
                >
                  {t("common:Chính sách bảo mật")}
                </a>
                &nbsp;{t("common:của Atadi")}.
              </p>
            </Form>
          </div>
        </div>
        {popupToShow === "ENTER_OTP" ? (
          <EnterOtp
            feature={VERIFICATION.FEATURE.REGISTER}
            type={otpType}
            verificationId={verificationId}
            value={
              otpType === "email"
                ? form.getFieldValue("username")
                : commonUtil.convertPhone(form.getFieldValue("username"))
            }
            onClose={() => {
              setPopupToShow(null);
            }}
            onResend={(verificationId) => {
              setVerificationId(verificationId);
            }}
            onConfirmSuccess={onConfirmOtpSuccess}
          />
        ) : null}
        {phoneNotVerify && popupToShow === "NOTIFY_PHONE" ? (
          <PhoneNotVerifyPopup
            isRegister={true}
            phone={phoneNotVerify.phone}
            emails={phoneNotVerify.emails}
            onCancel={() => {
              setPopupToShow(null);
              setPhoneNotVerify(undefined);
            }}
            onRegister={onClickContinueRegister}
          />
        ) : null}
      </div>
    </>
  );
};

export default Register;
